/**
 * Startseite – /
 *
 * Landing-Page für Nighttrade.
 * Sektionen: Navigation · Hero · Alpha-Banner · Warum Alpha? · Feature-Karten · Screenshots · Bottom-CTA
 *
 * Wird ausschließlich von Ausgeloggten gesehen: renderHome() leitet eingeloggte
 * Spieler sofort per navigate('/dashboard') weiter, bevor irgendein Markup
 * geschrieben wird (siehe unten) – daher keine Verzweigungslogik für Buttons/Nav.
 *
 * Der Footer ist NICHT Teil dieser Seite – der globale Footer
 * (components/footer.ts, #nt-footer) wird einmalig in main.ts::initApp() an
 * body gehängt und erscheint automatisch danach. Kein eigener Footer hier
 * anlegen (führte bereits einmal zu einer sichtbaren Dopplung, NT-LANDING-01).
 *
 * Stil: Terminal-UI, Dark-Theme, Share Tech Mono.
 * Kein Emoji. Kein UI-Gradient. Kein box-shadow mit Blur. border-radius max 4px.
 * Hero-Overlay-Gradient: bewusstes Design-Ausnahme, explizit im Prompt definiert.
 */

import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import '../styles/home.css';
import {
  ALPHA_WHY_TITLE,
  ALPHA_WHY_INTRO_PARAGRAPHS,
  ALPHA_WHY_TRIGGER_LABEL,
  ALPHA_WHY_MESSAGE_HEADING,
  ALPHA_WHY_MESSAGE_PARAGRAPHS,
  ALPHA_WHY_MEANING_ITEMS,
  ALPHA_WHY_SIGNATURE,
} from '../data/alpha-copy.js';

interface ScreenshotDef {
  readonly src: string;
  readonly alt: string;
  readonly caption: string;
  readonly width: number;
  readonly height: number;
}

const SCREENSHOTS: readonly ScreenshotDef[] = [
  {
    src: '/screenshots/jobs.png',
    alt: 'Job-Liste in Nighttrade mit mehreren Phase-1-Jobs wie Hühnerstreichler und Flaschensammler',
    caption: 'Karriere-Start: Hühner streicheln, Flaschen sammeln, Kazoo spielen.',
    width: 1600,
    height: 824,
  },
  {
    src: '/screenshots/schicht.png',
    alt: 'Laufende Job-Schicht mit Fortschrittsbalken und Countdown',
    caption: 'Schichten laufen im Hintergrund. Auch wenn du weg bist.',
    width: 1600,
    height: 826,
  },
  {
    src: '/screenshots/talent.png',
    alt: 'Talentbaum mit mehreren Talent-Zweigen und Lernzeit-Anzeige',
    caption: 'Talentbaum mit sechs Zweigen. Lernzeit statt Sofortkauf.',
    width: 1600,
    height: 824,
  },
];

export function renderHome(): void {
  // Bereits eingeloggt: direkt zum Dashboard – diese Seite wird von
  // eingeloggten Spielern nie gerendert.
  if (gameState.isAuthenticated()) {
    navigate('/dashboard');
    return;
  }

  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <!-- Navigation: nur Logo + Einloggen/Konto erstellen. Kein Menü zu
         SPIEL/RANKING/MARKT/COMMUNITY – diese Routen sind für Ausgeloggte
         entweder unregistriert (404) oder eine sofortige Login-Wand. -->
    <nav class="hn-nav" id="hn-nav">
      <a href="/" class="hn-nav-logo">
        <span class="hn-nav-logo-box">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="18" height="18" stroke="currentColor" stroke-width="1.5"/>
            <path d="M5 15V5L10 13L15 5V15" stroke="currentColor" stroke-width="1.5" fill="none"/>
          </svg>
        </span>
        NIGHTTRADE
      </a>
      <div class="hn-nav-actions">
        <a href="/login" class="hn-nav-login-link">Einloggen</a>
        <a href="/register" class="hn-nav-cta">KONTO ERSTELLEN</a>
      </div>
    </nav>

    <!-- Hero -->
    <section class="hn-hero" aria-label="Nighttrade Hero">
      <!-- HINTERGRUNDBILD: hero-bg.jpg im selben Ordner wie index.html
           Platzieren unter: apps/frontend/public/hero-bg.jpg
           Wird serviert als: /hero-bg.jpg -->
      <div class="hn-hero-overlay"></div>
      <div class="hn-hero-glow"></div>
      <div class="hn-hero-content">
        <p class="hn-hero-eyebrow">// NIGHTTRADE &mdash; CLOSED ALPHA</p>
        <h1 class="hn-hero-h1">VOM PARKW&Auml;CHTER<br>ZUM KARTELLCHEF.</h1>
        <p class="hn-hero-sub">
          Du f&auml;ngst mit einem Rasselbeutel an. Irgendwann geh&ouml;rt dir die Stadt.<br>
          Deutsches Idle-Spiel im Browser. Kostenlos, keine Installation.
        </p>
        <div class="hn-hero-ctas">
          <a href="/register" class="hn-btn hn-btn--red">
            <span class="hn-pulse-dot" aria-hidden="true"></span>
            KONTO ERSTELLEN
          </a>
          <a href="/login" class="hn-hero-login-link">Bereits registriert? Einloggen</a>
          <p class="hn-hero-hint">Closed Alpha &mdash; Pl&auml;tze begrenzt</p>
        </div>
      </div>
    </section>

    <!-- Alpha-Banner -->
    <div class="hn-alpha-banner">
      <span class="hn-alpha-badge">
        <span class="hn-pulse-dot" aria-hidden="true"></span>
        CLOSED ALPHA
      </span>
      <span class="hn-alpha-banner-text">
        Bereits registriert?
        <a href="/login" class="hn-alpha-banner-link">Einloggen</a>
        &nbsp;&middot;&nbsp;
        Noch kein Konto?
        <a href="/register" class="hn-alpha-banner-link">Konto erstellen</a>
      </span>
    </div>

    <!-- Warum Alpha? -->
    <section class="hn-alpha-why" aria-label="Warum Alpha?">
      <div class="hn-alpha-why-collapse" id="hn-alpha-why-collapse">
        <h2 class="hn-section-title">${ALPHA_WHY_TITLE}</h2>
        ${ALPHA_WHY_INTRO_PARAGRAPHS.map((p) => `<p class="hn-alpha-why-teaser">${p}</p>`).join('')}
        <button type="button" class="hn-alpha-why-trigger" id="hn-alpha-why-trigger" aria-expanded="false" aria-controls="hn-alpha-why-body">
          <span class="hn-alpha-why-arrow" aria-hidden="true">&#9654;</span>
          ${ALPHA_WHY_TRIGGER_LABEL}
        </button>
        <div class="hn-alpha-why-body" id="hn-alpha-why-body">
          <h3 class="hn-alpha-why-subheading">${ALPHA_WHY_MESSAGE_HEADING}</h3>
          ${ALPHA_WHY_MESSAGE_PARAGRAPHS.map((p) => `<p class="hn-alpha-why-text">${p}</p>`).join('')}
          ${ALPHA_WHY_MEANING_ITEMS.map((p) => `<p class="hn-alpha-why-text">${p}</p>`).join('')}
          <p class="hn-alpha-why-signature">${ALPHA_WHY_SIGNATURE}</p>
        </div>
      </div>
    </section>

    <!-- Feature-Karten -->
    <section class="hn-features" aria-label="Features">
      <div class="hn-features-header">
        <p class="hn-section-label">// DAS GAME</p>
        <h2 class="hn-section-title">WAS DICH ERWARTET</h2>
      </div>
      <div class="hn-features-grid">
        <article class="hn-feature-card">
          <span class="hn-feature-num">01</span>
          <h3 class="hn-feature-name">JOBS</h3>
          <p class="hn-feature-desc">
            H&uuml;hnerstreichler, Kazoo-Spieler, Klo-Reiniger Bahnhof.
            80 Jobs &uuml;ber f&uuml;nf Karriere-Phasen.
          </p>
        </article>
        <article class="hn-feature-card">
          <span class="hn-feature-num">02</span>
          <h3 class="hn-feature-name">TALENTE</h3>
          <p class="hn-feature-desc">
            Gr&uuml;ner Daumen, Schuppenmeister, Nebenjobber.
            Lernzeit l&auml;uft im Hintergrund weiter.
          </p>
        </article>
        <article class="hn-feature-card">
          <span class="hn-feature-num">03</span>
          <h3 class="hn-feature-name">ZWEI W&Auml;HRUNGEN</h3>
          <p class="hn-feature-desc">
            Sauberes Geld und Schwarzgeld. B&ouml;rse, Markt, Schwarzmarkt, Geldw&auml;sche.
          </p>
        </article>
        <article class="hn-feature-card">
          <span class="hn-feature-num">04</span>
          <h3 class="hn-feature-name">IDLE</h3>
          <p class="hn-feature-desc">
            L&auml;uft weiter, wenn du nicht da bist. Offline-Einkommen mit fairem Cap.
          </p>
        </article>
      </div>
    </section>

    <!-- Screenshots -->
    <section class="hn-shots" aria-label="Screenshots aus dem Spiel">
      <div class="hn-features-header">
        <p class="hn-section-label">// IN-GAME</p>
        <h2 class="hn-section-title">SO SIEHT'S AUS</h2>
      </div>
      <div class="hn-shots-grid">
        ${SCREENSHOTS.map(
          (shot) => `
          <figure class="hn-shot-card">
            <button type="button" class="hn-shot-btn" data-shot-src="${shot.src}" data-shot-alt="${shot.alt}">
              <img
                src="${shot.src}"
                alt="${shot.alt}"
                width="${String(shot.width)}"
                height="${String(shot.height)}"
                loading="lazy"
                class="hn-shot-img"
              />
            </button>
            <figcaption class="hn-shot-caption">${shot.caption}</figcaption>
          </figure>
        `
        ).join('')}
      </div>
    </section>

    <!-- Lightbox (leer bis zum ersten Klick) -->
    <div class="hn-lightbox" id="hn-lightbox" aria-hidden="true">
      <button type="button" class="hn-lightbox-close" id="hn-lightbox-close" aria-label="Schlie&szlig;en">&times;</button>
      <img src="" alt="" class="hn-lightbox-img" id="hn-lightbox-img" />
    </div>

    <!-- Bottom CTA -->
    <section class="hn-bottom-cta">
      <h2 class="hn-bottom-cta-title">BEREIT EINZUSTEIGEN?</h2>
      <div class="hn-bottom-cta-buttons">
        <a href="/register" class="hn-btn hn-btn--red">
          <span class="hn-pulse-dot" aria-hidden="true"></span>
          KONTO ERSTELLEN
        </a>
      </div>
      <a href="/login" class="hn-hero-login-link">Bereits registriert? Einloggen</a>
    </section>
  `;

  attachLightboxHandler();
  attachAlphaWhyToggleHandler();
}

// document-Listener nur einmal je Session anhängen – innerHTML ersetzt bei
// jedem renderHome()-Aufruf die Buttons/das Lightbox-Element selbst (deren
// eigene Listener verschwinden dadurch automatisch mit dem alten DOM-Knoten),
// aber ein an document gebundener Listener würde sich sonst bei jedem
// erneuten Besuch von "/" in derselben Session duplizieren.
let escapeHandlerRegistered = false;

/** Klick auf ein Screenshot-Thumbnail öffnet es groß, ESC/Klick daneben schließt. */
function attachLightboxHandler(): void {
  const lightbox = document.getElementById('hn-lightbox');
  const lightboxImg = document.getElementById('hn-lightbox-img') as HTMLImageElement | null;
  const closeBtn = document.getElementById('hn-lightbox-close');
  if (!lightbox || !lightboxImg || !closeBtn) return;

  const box = lightbox;
  const img = lightboxImg;

  function open(src: string, alt: string): void {
    img.src = src;
    img.alt = alt;
    box.classList.add('hn-lightbox--open');
    box.setAttribute('aria-hidden', 'false');
  }

  function close(): void {
    box.classList.remove('hn-lightbox--open');
    box.setAttribute('aria-hidden', 'true');
    img.src = '';
  }

  document.querySelectorAll<HTMLButtonElement>('.hn-shot-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const src = btn.dataset.shotSrc ?? '';
      const alt = btn.dataset.shotAlt ?? '';
      if (src) open(src, alt);
    });
  });

  closeBtn.addEventListener('click', close);
  box.addEventListener('click', (e) => {
    if (e.target === box) close();
  });

  if (!escapeHandlerRegistered) {
    escapeHandlerRegistered = true;
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const openBox = document.querySelector('.hn-lightbox--open');
      if (!openBox) return;
      openBox.classList.remove('hn-lightbox--open');
      openBox.setAttribute('aria-hidden', 'true');
    });
  }
}

/**
 * "WARUM ALPHA?"-Sektion: Trigger klappt den Body auf/zu. Lokaler Nachbau
 * des push-categories-Patterns aus settings.ts (Wrapper-Modifier-Klasse
 * treibt gleichzeitig Pfeil-Rotation + Body-Sichtbarkeit) – kein Cross-Page-
 * Import, da home.ts eine eigenständige Seite ist.
 */
function attachAlphaWhyToggleHandler(): void {
  const collapse = document.getElementById('hn-alpha-why-collapse');
  const trigger = document.getElementById('hn-alpha-why-trigger');
  if (!collapse || !trigger) return;

  trigger.addEventListener('click', () => {
    const isOpen = collapse.classList.toggle('hn-alpha-why-collapse--open');
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
}
